import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { CalendarDays } from 'lucide-react';
import { DateRangePresets } from '../components/DateRangePresets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METHODS = [['cash', 'מזומן'], ['credit', 'אשראי'], ['check', 'שיק'], ['transfer', 'העברה']];
const money = (v) => `₪${(parseFloat(v) || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' });

const Stat = ({ label, value, tone, testId }) => (
  <div className={`rounded-xl border-2 p-4 ${tone}`} data-testid={testId}>
    <p className="text-xs font-semibold opacity-80">{label}</p>
    <p className="text-2xl font-black mt-1">{value}</p>
  </div>
);

const Row = ({ r, th, td }) => (
  <tr className="hover:bg-emerald-50" data-testid={`daily-income-row-${r.date}`}>
    <td className={`${td} font-semibold whitespace-nowrap`}>{dayLabel(r.date)}</td>
    <td className={`${td} font-bold text-emerald-700`}>{money(r.received)}</td>
    {METHODS.map(([k]) => <td key={k} className={`${td} text-gray-600`}>{r.byMethod[k] ? money(r.byMethod[k]) : '-'}</td>)}
    <td className={`${td} text-blue-700 font-semibold`}>{money(r.sales)}</td>
    <td className={`${td} text-orange-700`}>{money(r.cost)}</td>
    <td className={`${td} font-bold ${r.profit < 0 ? 'text-red-600' : 'text-green-700'}`}>{money(r.profit)}</td>
  </tr>
);

export default function DailyIncomeReport() {
  const now = new Date();
  const [range, setRange] = useState([fmt(new Date(now.getFullYear(), now.getMonth(), 1)), fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0))]);
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reports/daily-income`, { params: { start: range[0], end: range[1] } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range]);

  const th = 'p-3 text-center font-bold border border-gray-300 whitespace-nowrap';
  const td = 'p-3 text-center border border-gray-200';
  const t = data?.totals;

  return (
    <div className="min-h-screen py-10 px-4" dir="rtl" data-testid="daily-income-page">
      <div className="container mx-auto max-w-6xl space-y-6">
        <h1 className="text-4xl font-black text-gray-900 flex items-center gap-3"><CalendarDays className="w-9 h-9 text-emerald-600" />הכנסות לפי יום</h1>
        <Card className="shadow-lg print:hidden">
          <CardContent className="p-5">
            <DateRangePresets startDate={range[0]} endDate={range[1]} onChange={(s, e) => setRange([s, e])} />
            <div className="flex flex-wrap gap-3 items-center">
              <label className="text-sm font-semibold">מתאריך</label>
              <input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="p-2 border-2 rounded-md" data-testid="daily-income-start" />
              <label className="text-sm font-semibold">עד תאריך</label>
              <input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="p-2 border-2 rounded-md" data-testid="daily-income-end" />
            </div>
          </CardContent>
        </Card>

        {t && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="כסף שנכנס בפועל" value={money(t.received)} tone="bg-emerald-50 border-emerald-300 text-emerald-800" testId="daily-income-total-received" />
            <Stat label='עבודות שבוצעו (לפני מע"מ)' value={money(t.sales)} tone="bg-blue-50 border-blue-300 text-blue-800" testId="daily-income-total-sales" />
            <Stat label="עלות חלקים" value={money(t.cost)} tone="bg-orange-50 border-orange-300 text-orange-800" testId="daily-income-total-cost" />
            <Stat label="רווח" value={money(t.profit)} tone="bg-green-50 border-green-300 text-green-800" testId="daily-income-total-profit" />
          </div>
        )}

        <Card className="shadow-lg">
          <CardContent className="p-0 overflow-x-auto">
            {!data?.days?.length ? <p className="p-10 text-center text-gray-400" data-testid="daily-income-empty">אין הכנסות או עבודות בטווח התאריכים</p> : (
              <table className="w-full border-collapse text-sm" data-testid="daily-income-table">
                <thead className="bg-emerald-600 text-white">
                  <tr>
                    <th className={th}>יום</th><th className={th}>נכנס בפועל</th>
                    {METHODS.map(([k, l]) => <th key={k} className={th}>{l}</th>)}
                    <th className={th}>עבודות שבוצעו</th><th className={th}>עלות</th><th className={th}>רווח</th>
                  </tr>
                </thead>
                <tbody>{data.days.map((r) => <Row key={r.date} r={r} th={th} td={td} />)}</tbody>
                <tfoot className="bg-emerald-100 font-black">
                  <tr data-testid="daily-income-totals-row">
                    <td className={td}>סה"כ</td><td className={td}>{money(t.received)}</td>
                    {METHODS.map(([k]) => <td key={k} className={td}>{money(t.byMethod[k])}</td>)}
                    <td className={td}>{money(t.sales)}</td><td className={td}>{money(t.cost)}</td><td className={td}>{money(t.profit)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
